<template>
  <div>
    <v-tabs fixed-tabs slot="extension" v-model="selectedTab">
      <v-tabs-items v-model="selectedTab">
        <v-tab-item value="tab-input" key="input">
          <v-container fluid>
            <v-layout justify-center align-start class="hidden-sm-and-down">
              <v-flex xs12>
                <v-spacer></v-spacer>
              </v-flex>
            </v-layout>
            <v-layout>
              <v-flex xs9 id="annotate_flex">
                <v-textarea
                    v-if="!loadingText"
                    id="annotate_area"
                    name="input-textarea"
                    v-bind:label="$t('annotate.label.inputtext')"
                    v-model="text"
                    auto-grow
                    outlined
                    clearable
                    rows="10"
                >
                  <template v-if="canSeeAdvancedSettings" v-slot:prepend-inner>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on }">
                        <v-btn
                            v-on="on"
                            @click="$refs.fileToLoad.click()"
                            color="primary"
                        >
                          {{ $t("app.button.import") }}
                        </v-btn>
                      </template>
                      {{ $t("app.tooltip.testFileOnDisk") }}
                    </v-tooltip>
                  </template>
                </v-textarea>
                <v-progress-circular
                    v-else
                    indeterminate
                    :size="70"
                    color="primary"
                    style="margin-left: 45%; margin-top: 100px"
                ></v-progress-circular>
              </v-flex>
              <v-flex xs2>
                <v-layout align-start justify-start column>
                  <v-flex ml-3 id="selectedAnnotator">
                    <v-select
                        v-model="selectedAnnotator"
                        :items="localizedAnnotatorsSelectData"
                        :menu-props="{ maxHeight: 450 }"
                        outlined
                        single-line
                        color="blue-grey lighten-2"
                        label="Select"
                        item-text="label"
                        item-value="name"
                    >
                      <template slot="selection" slot-scope="data">
                        {{ data.item.label }}
                      </template>
                      <template slot="item" slot-scope="data">
                        <template v-if="typeof data.item !== 'object'">
                          <v-list-item-content v-text="data.item"></v-list-item-content>
                        </template>
                        <template v-else>
                          <v-list-item-content :id="`sugChoice-${data.item.label}`">
                            <v-list-item-title v-html="`<span  style='margin-left: 20px;'>${data.item.label}</span>`"></v-list-item-title>
                            <v-list-item-subtitle v-html="data.item.group"></v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </template>
                    </v-select>
                  </v-flex>
                  <v-flex ml-2>
                    <v-btn
                        :disabled="loadingText || !selectedAnnotator"
                        id="btn_annotate"
                        color="primary"
                        :loading="annotating"
                        @click="annotate()"
                    >
                      {{ $t("app.button.annotate") }}
                    </v-btn>
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
            <v-layout justify-center align-start wrap class="hidden-sm-and-up">
              <v-flex xs12>
                <v-textarea
                    id="annotate_area_mob"
                    name="input-textarea"
                    v-bind:label="$t('annotate.label.inputtext')"
                    v-model="text"
                    auto-grow
                    outlined
                    clearable
                    rows="10"
                ></v-textarea>
              </v-flex>
              <v-flex>
                <v-btn
                    :disabled="loadingText || !selectedAnnotator"
                    id="btn_annotate_mob"
                    color="primary"
                    :loading="annotating"
                    @click="annotate()"
                >
                  {{ $t("app.button.annotate") }}
                </v-btn>
              </v-flex>
            </v-layout>
          </v-container>
        </v-tab-item>
        <v-tab-item value="tab-result" key="result">
          <v-container>
            <v-layout justify-center>
              <v-flex xs9>
                <TestResultCard
                    id="annotatedText"
                    v-if="doc"
                    :doc="doc"
                ></TestResultCard>
              </v-flex>
              <v-flex xs2 px-5>
                <v-btn id="btn_back" color="primary" fab dark @click="back()">
                  <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
              </v-flex>
            </v-layout>
          </v-container>
        </v-tab-item>
      </v-tabs-items>
    </v-tabs>
    <input
        accept=".txt"
        type="file"
        v-on:change="loadTextFromFile"
        ref="fileToLoad"
        style="display: none"
    />
  </div>
</template>

<script>
import TestResultCard from "./TestResultCard";
import tourMixin from "../../tourUtils/tourMixin";
import {mapState, mapGetters, mapMutations, mapActions} from "vuex";
import {mapProperty} from "@/utils/vuexx";
import {api, project} from "@/api";
import {betterEncodeURIComponent} from "@/routes/pathParams";

export default {
  props: ["testParams"],
  components: {
    TestResultCard,
  },
  provide: function () {
    return {
      importFiles: this.importTestFile,
      closeClicked: this.closeClicked,
    };
  },
  data() {
    return {
      loadingText: false,
      selectedTab: "tab-input",
      annotating: false,
      fileName: undefined,
    };
  },
  created() {
    tourMixin.enhance(this, "tourNext");
    tourMixin.decorate(this, "annotationDone");
    this.tourNext();
  },
  mounted() {
    this.getAnnotatorsByType();
    this.preloadInputText(this.testParams);
  },
  beforeDestroy() {
    this.setAnnotDetailsDrawer(false);
  },
  watch: {
    testParams: function (testParams) {
      this.preloadInputText(testParams);
    },
  },
  computed: {
    ...mapState("experiments", ["experiments"]),
    ...mapState("test", ["doc", "annotator"]),
    ...mapGetters("auth", ["canImportDocuments", "canSeeAdvancedSettings"]),
    ...mapGetters("test", ["localizedAnnotatorsSelectData"]),
    selectedAnnotator: mapProperty("test", "annotator"),
    text: {
      get() {
        return this.$store.state.test.text;
      },
      set(value) {
        this.$store.commit("test/setText", value);
      },
    },
  },
  methods: {
    ...mapMutations(["setAnnotDetailsDrawer"]),
    ...mapMutations("test", ["setAnnotator", "setText"]),
    ...mapActions("test", ["annotateDocument", "getAnnotatorsByType"]),
    loadFile(fileReaderEvent) {
      this.setText(fileReaderEvent.result);
    },
    loadTextFromFile(ev) {
      const file = ev;
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result.replace(/\r/g, "")
        this.setText(text);
        // this.setText(e.target.result);
      };
      reader.readAsText(file.target.files[0]);
    },
    async annotate() {
      this.annotating = true;
      try {
        await this.annotateDocument(this.text)
        this.annotationDone();
      } finally {
        this.annotationFailed()
        this.annotating = false;
      }
    },
    // eslint-disable-next-line
    annotationFailed() {
      this.annotating = false;
    },
    // eslint-disable-next-line
    annotationDone() {
      this.selectedTab = "tab-result";
      this.annotating = false;
    },
    back() {
      this.setAnnotDetailsDrawer(false);
      this.annotating = false;
      this.selectedTab = "tab-input";
    },
    async preloadInputText(testParams) {
      if (testParams && testParams.docId) {
        this.loadingText = true;
        try {
          if (testParams.start >= 0) {
            await this.getSegmentText(testParams.docId, testParams.start);
          } else {
            await this.getDocumentText(testParams.docId);
          }
        } finally {
          this.loadingText = false;
        }
      }
    },
    async getDocumentText(docId) {
      try {
        const url = `/projects/${project}/documents/${betterEncodeURIComponent(docId)}?htmlVersion=false&outputFields=text`
        const response = await api.get(url);
        this.setText(response.data.text);
      } catch (error) {
        this.$store.addFadingApiErrorNotification(
            this.$i18n.t("error.getDocument"),
            error
        );
      }
    },
    async getSegmentText(docId, start) {
      try {
        const finalQuery = `+documentIdentifier:"${docId}" +start:${start}`;
        const url = `/projects/${project}/segments/_search?query=${betterEncodeURIComponent(finalQuery)}&facet=false&highlight=false&from=0&size=1&htmlVersion=false`;
        const response = await api.post(url);
        this.setText(response.data.hits[0].segment.text);
      } catch (error) {
        this.$store.addFadingApiErrorNotification(
            this.$i18n.t("error.getSegment"),
            error
        );
      }
    },
  }
};
</script>

<style>
/* Specifically for the test page, overwrite red cross */
#testtext span.kt_an {
  padding-right: 0.5em;
}

#testtext a.kt_del_an {
  display: none;
}
</style>